import React, {memo, useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C133_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C133TimetableViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

type C133Row = (typeof C133_PAGE.rows)[number];

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.7rem',
    py: 0.75,
    px: 0.4,
    lineHeight: 1.25,
    verticalAlign: 'bottom' as const,
    whiteSpace: 'pre-line' as const,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.4,
    verticalAlign: 'middle' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

const CellInput = memo(function CellInput({
    value,
    onChange,
    align = 'left',
}: {
    value: string;
    onChange: (v: string) => void;
    align?: 'left' | 'center';
}) {
    return (
        <Box
            component="input"
            value={value}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
});

const CellTextarea = memo(function CellTextarea({
    value,
    onChange,
}: {
    value: string;
    onChange: (v: string) => void;
}) {
    return (
        <Box
            component="textarea"
            value={value}
            rows={2}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, resize: 'vertical', minHeight: 48}}
        />
    );
});

const DateRange = memo(function DateRange({
    from,
    to,
    onFrom,
    onTo,
}: {
    from: string;
    to: string;
    onFrom: (v: string) => void;
    onTo: (v: string) => void;
}) {
    return (
        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0}}>
            <CellInput value={from} onChange={onFrom} align="center"/>
            <Typography variant="caption" color="text.secondary" sx={{flexShrink: 0}}>
                to
            </Typography>
            <CellInput value={to} onChange={onTo} align="center"/>
        </Box>
    );
});

const TimetableRow = memo(function TimetableRow({
    row,
    values,
    onChange,
}: {
    row: C133Row;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}) {
    const f = (k: string) => values[`${row.id}.${k}`] ?? '';
    const set = useCallback((k: string) => (v: string) => onChange(`${row.id}.${k}`, v), [onChange, row.id]);

    if (row.fields === 'none') {
        return (
            <TableRow>
                <TableCell sx={bodySx}>
                    <Typography variant="body2" fontWeight={700}>
                        {row.num}
                    </Typography>
                </TableCell>
                <TableCell sx={bodySx} colSpan={3}>
                    <Typography variant="body2" fontWeight={700}>
                        {row.label}
                    </Typography>
                </TableCell>
            </TableRow>
        );
    }

    if (row.fields === 'text') {
        return (
            <TableRow>
                <TableCell sx={bodySx}>
                    <Typography variant="body2" fontWeight={600}>
                        {row.num}
                    </Typography>
                </TableCell>
                <TableCell sx={bodySx}>
                    <Typography variant="body2">{row.label}</Typography>
                </TableCell>
                <TableCell sx={bodySx} colSpan={2}>
                    <CellTextarea value={f('text')} onChange={set('text')}/>
                </TableCell>
            </TableRow>
        );
    }

    const indent = row.kind === 'sub';
    const showName = row.fields === 'nameDates';

    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" fontWeight={600} sx={{pl: indent ? 1 : 0}}>
                    {row.num}
                </Typography>
            </TableCell>
            <TableCell sx={bodySx}>
                {showName ? (
                    <Box sx={{pl: indent ? 1.5 : 0}}>
                        <CellInput value={f('name')} onChange={set('name')}/>
                    </Box>
                ) : (
                    <Typography variant="body2" sx={{pl: indent ? 1.5 : 0}}>
                        {row.label}
                    </Typography>
                )}
            </TableCell>
            <TableCell sx={bodySx}>
                <DateRange
                    from={f('plannedFrom')}
                    to={f('plannedTo')}
                    onFrom={set('plannedFrom')}
                    onTo={set('plannedTo')}
                />
            </TableCell>
            <TableCell sx={bodySx}>
                <DateRange
                    from={f('actualFrom')}
                    to={f('actualTo')}
                    onFrom={set('actualFrom')}
                    onTo={set('actualTo')}
                />
            </TableCell>
        </TableRow>
    );
});

/** Excel C13.3 — Assignment Planning Timetable */
export default function C133TimetableView({header, values, onChange}: C133TimetableViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const stableOnChange = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C13.3"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 0.5, mt: 0.5}}
            >
                {C133_PAGE.title}
            </Typography>
            {C133_PAGE.objective ? (
                <Typography variant="body2" sx={{mb: 1.25, fontStyle: 'italic'}}>
                    {C133_PAGE.objective}
                </Typography>
            ) : null}

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 920,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '5%'}}/>
                        <col style={{width: '35%'}}/>
                        <col style={{width: '30%'}}/>
                        <col style={{width: '30%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            <TableCell sx={headSx} align="center">
                                {C133_PAGE.plannedHeader}
                            </TableCell>
                            <TableCell sx={headSx} align="center">
                                {C133_PAGE.actualHeader}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C133_PAGE.rows.map((row) => (
                            <TimetableRow
                                key={row.id}
                                row={row}
                                values={values}
                                onChange={stableOnChange}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
